import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { DrawerContent, DrawerRoot, DrawerTitle } from '@components/ui/drawer';
import { Button } from '@components/ui/button';
import { formatTime } from '@utils/i18n';
import { cn } from '@utils/cn';
import { TimeWheel } from '../timeWheel';
import { changeEnd, changeStart } from '../timeRangePicker/helpers';
import { PhoneTimeRangeProps, TimeEdge } from './types';

const toDate = (minutes: number) => new Date(2000, 0, 1, Math.floor(minutes / 60), minutes % 60);

export const PhoneTimeRange = ({ value, onChange, disabled }: PhoneTimeRangeProps) => {
  const { t } = useTranslation('app');
  const [edge, setEdge] = useState<TimeEdge | null>(null);
  const [draft, setDraft] = useState(0);
  const labels = { start: t('forms.startTime.label'), end: t('forms.endTime.label') };

  const openEdge = (next: TimeEdge) => {
    setDraft(value[next]);
    setEdge(next);
  };

  const apply = () => {
    if (edge) onChange(edge === 'start' ? changeStart(value, draft) : changeEnd(value, draft));
    setEdge(null);
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        {(['start', 'end'] as const).map((key) => (
          <button
            key={key}
            type="button"
            disabled={disabled}
            aria-haspopup="dialog"
            aria-expanded={edge === key}
            onClick={() => openEdge(key)}
            className={cn(
              'flex h-14 items-center gap-2 rounded-md border-[1.5px] border-neutral-300 bg-white pl-3 pr-2.5 text-left disabled:bg-neutral-50',
              edge === key && 'border-2 border-primary-500',
            )}>
            <span className="flex min-w-0 flex-1 flex-col leading-tight">
              <span className="text-xs text-neutral-500">{labels[key]}</span>
              <span className="text-[17px] font-semibold tabular-nums text-neutral-800">
                {formatTime(toDate(value[key]))}
              </span>
            </span>
            <ChevronDown className="h-5 w-5 shrink-0 text-neutral-500" />
          </button>
        ))}
      </div>
      <DrawerRoot
        open={Boolean(edge)}
        direction="bottom"
        onOpenChange={(open) => {
          if (!open) setEdge(null);
        }}>
        <DrawerContent
          aria-describedby={undefined}
          overlayClassName="z-[70] bg-[rgba(24,28,33,.45)]"
          className="z-[70] gap-3 rounded-t-2xl border-0 px-4 pb-[calc(16px+env(safe-area-inset-bottom,0px))] pt-2 shadow-[0_-8px_30px_rgba(0,0,0,.18)]">
          <div className="mx-auto h-1.5 w-10 shrink-0 rounded-full bg-neutral-200" />
          <DrawerTitle className="text-center text-[17px] font-bold text-neutral-800">
            {edge && labels[edge]}
          </DrawerTitle>
          {edge && <TimeWheel label={labels[edge]} value={draft} onChange={setDraft} />}
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="secondary"
              className="h-11 justify-center"
              onClick={() => setEdge(null)}>
              {t('actions.cancel')}
            </Button>
            <Button className="h-11 justify-center" onClick={apply}>
              {t('actions.done')}
            </Button>
          </div>
        </DrawerContent>
      </DrawerRoot>
    </>
  );
};
